import type { SessionStatus } from "@hakili/app-shell";
import { type ClientIdentity, clientIdentityFrom } from "@hakili/contracts";

/**
 * `P1-CLW-007` and `P1-CLW-008`: the client's session, as a machine that needs
 * no browser.
 *
 * **The same doctrine the console was given, for the same reason.** The access
 * token is held in memory and nowhere else , not `localStorage`, not
 * `sessionStorage`, not a readable cookie, not a URL. A bearer token in storage
 * survives the tab that earned it and is readable by any script that reaches the
 * origin; a token in a closure dies with the page. The REFRESH authority lives
 * in the `SameSite=Strict` cookie the API sets, which is what `A41`'s same-site
 * hostnames were bought for and why `app-staging.hakili.finance` calling
 * `api-staging.hakili.finance` can recover a session this module forgot.
 *
 * **The shell store is `localStorage` and the token is not, and the two are not
 * in tension.** 4.6.5 puts a buyer's half-filled form in `localStorage` because
 * *"a client loses his connection mid-form and comes back on the same
 * handset"*. A form is his own text; a bearer token is an authority. One
 * belongs on the handset and the other does not.
 *
 * **Why a machine rather than a hook.** `P0-UIK-016` and its siblings ticked
 * green on components nothing mounted, and the deploy sitting of 24 August 2026
 * found that `main.tsx` rendered `null`. Behaviour buried in a component can
 * only be tested by rendering it. So every transition is a function from a state
 * to a state, testable with no DOM, and the hook is the thin part.
 */

/**
 * WF-CLI-01 is a five-screen wizard and WF-CLI-02 is one screen, and the
 * anonymous state carries WHICH of them a person is standing in.
 *
 * The names are the workflow's own screen names, so a reader comparing this file
 * to 03 does not have to translate: `bienvenue`, `numero`, `code`, `profil`,
 * `choixcode`, `connexion`.
 */
export type EtapeAnonyme =
  "bienvenue" | "numero" | "code" | "profil" | "compteExistant" | "choixcode" | "connexion";

export type Session =
  | { readonly status: "pending" }
  | {
      readonly status: "anonymous";
      readonly etape: EtapeAnonyme;
      /** The number being verified, carried between screens. */
      readonly phone: string;
      /** Held only between `otp/verify` and `register`. Never stored. */
      readonly enrolmentToken: string | null;
      /** The server's own sentence when there is one. 4.5.5 writes it beside the guard. */
      readonly refusal?: string;
      /** WF-CLI-01, step 3: which channel actually carried the code. */
      readonly canal?: string;
    }
  | { readonly status: "authenticated"; readonly identity: ClientIdentity };

export const START: Session = { status: "pending" };

/** What `AuthGate` reads. The shell knows three words; this machine knows why. */
export const statusOf = (session: Session): SessionStatus => session.status;

export const anonyme = (etape: EtapeAnonyme, phone = ""): Session => ({
  status: "anonymous",
  etape,
  phone,
  enrolmentToken: null,
});

/**
 * A step forward that KEEPS the number and the token.
 *
 * The wizard's whole failure mode is losing them: a person who typed a number,
 * received a code and then found the app asking for the number again has been
 * made to do the work twice, and on 3G that is where they stop.
 */
export interface Avance {
  readonly phone?: string;
  readonly enrolmentToken?: string | null;
  readonly canal?: string;
}

export const avance = (session: Session, etape: EtapeAnonyme, patch: Avance = {}): Session => {
  if (session.status !== "anonymous") return session;
  // `refusal` is DROPPED rather than carried: a refusal belongs to the screen
  // that produced it, and a person who has moved on should not find it waiting
  // for them on the next one.
  const reste = { ...session };
  delete (reste as { refusal?: string }).refusal;
  return { ...reste, ...patch, etape };
};

export const refuse = (session: Session, refusal: string): Session =>
  session.status !== "anonymous" ? session : { ...session, refusal };

export const entre = (identity: ClientIdentity): Session => ({ status: "authenticated", identity });

/**
 * WF-CLI-01, step 3: *"`Confirmer` moves to `profil` for a new account (no name
 * yet) or to `choixcode` otherwise."*
 *
 * The API says which by the SHAPE of its answer , an enrolment token for a
 * number it has never seen, a token answer for one it has. Reading the shape
 * rather than a flag is what keeps the two sides from disagreeing about who is
 * new.
 */
/**
 * Where the buyer goes after the code is verified, read off the SHAPE of the API's answer (see `estNouveauCompte`): a number never seen goes to
 * `profil`; a known one comes back signed in and goes to `compteExistant` (`S5-1 (a)`): the next step, `choixcode`, overwrites the account's PIN,
 * so it is reached only by an explicit confirm on the screen that says so.
 */
export const stepAfterVerification = (answer: unknown): EtapeAnonyme =>
  estNouveauCompte(answer) ? "profil" : "compteExistant";

export const estNouveauCompte = (answer: unknown): answer is { enrolmentToken: string } =>
  typeof answer === "object" &&
  answer !== null &&
  "enrolmentToken" in answer &&
  typeof answer.enrolmentToken === "string";

/**
 * The server's own sentence, preferred over any this app could invent.
 *
 * 4.5.5 writes the refusal beside the guard that produced it , *"Choisissez un
 * code moins facile à deviner"*, *"Trop de tentatives"* , and a screen that
 * rephrased them would be a second author of a sentence a buyer reads while
 * stuck. The fallback is for a transport failure, which has no sentence.
 */
export function messageOf(failure: unknown, fallback: string): string {
  const message = (failure as { message?: unknown } | null)?.message;
  return typeof message === "string" && message !== "" ? message : fallback;
}

/** `/me`'s answer, read defensively: a shape this app does not know is a refusal, never a crash. */
export function identityFrom(answer: unknown): ClientIdentity | null {
  // **The reader lives with the contract, in `@hakili/contracts`.** It used to
  // live here and read `fullName` and `phone` at the TOP LEVEL of `/api/v1/me`,
  // while the server has nested them under `profile` since `P0-IDN-012`; the two
  // had never agreed, so every real answer parsed to `null` and `useSession.ts`
  // returned the buyer to the landing with nothing thrown and nothing logged.
  // Round 184 moved the parsing beside the shape it parses, so there is one
  // reader rather than one per consumer, and `me-contract.e2e.spec.ts` runs it
  // against a LIVE answer , see `A48`.
  return clientIdentityFrom(answer);
}
